import ExcelJS from 'exceljs'
import { fetchGroupRows, fetchGroupTotals } from './groupRows'
import { buildGroupSheet, type SheetLine } from './groupSheet'
import { downloadBlob } from './transportNote'

// Excel for finance: re-reads the group's rows and totals from the database at the moment of export (never the screen's copy) and writes
// exactly what buildGroupSheet returns. Numbers stay numbers, so finance can sum or pivot them.
const WIDTHS = [18, 16, 11, 24, 24, 26, 8, 8, 9, 7, 14, 18, 10]
const fill = (argb: string) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } }) as ExcelJS.Fill

export async function exportGroupExcel(group: { id: string; ref: string; customerName: string | null }, generatedBy: string | null): Promise<void> {
  const [rows, totals] = await Promise.all([fetchGroupRows(group.id), fetchGroupTotals(group.id)])
  const now = new Date()
  const lines: SheetLine[] = buildGroupSheet({
    groupRef: group.ref, customerName: group.customerName, generatedBy,
    generatedAt: now.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
  }, rows, totals)

  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Shipments', { views: [{ state: 'frozen', ySplit: lines.findIndex(l => l.kind === 'header') + 1 }] })
  ws.columns = WIDTHS.map(width => ({ width }))
  for (const line of lines) {
    const r = ws.addRow(line.cells)
    if (line.kind === 'title') r.font = { bold: true, size: 14 }
    if (line.kind === 'note') r.font = { italic: true, color: { argb: 'FF666666' } }
    if (line.kind === 'warning') { r.font = { bold: true, color: { argb: 'FFB00020' } }; r.fill = fill('FFFDE8E8') }
    if (line.kind === 'header') { r.font = { bold: true, color: { argb: 'FFFFFFFF' } }; r.fill = fill('FF1F4E79') }
    if (line.kind === 'section') { r.font = { bold: true }; r.fill = fill('FFDDEBF7') }
    if (line.kind === 'subtotal') { r.font = { bold: true }; r.fill = fill('FFF2F2F2'); r.border = { top: { style: 'thin' } } }
    if (line.kind === 'total') { r.font = { bold: true }; r.fill = fill('FFD9D9D9'); r.border = { top: { style: 'medium' }, bottom: { style: 'double' } } }
    if (line.kind === 'row' || line.kind === 'subtotal' || line.kind === 'total') {
      r.getCell(9).numFmt = '#,##0'; r.getCell(10).numFmt = '#,##0'
      r.getCell(11).numFmt = '#,##0.###'; r.getCell(12).numFmt = '#,##0.###'; r.getCell(13).numFmt = '#,##0.0000'
    }
  }
  const buf = await wb.xlsx.writeBuffer()
  const day = now.toISOString().slice(0, 10)
  downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${group.ref}-${day}.xlsx`)
}
